import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, LifeBuoy } from "lucide-react";

const NAV = [
  { to: "/que-es-pasta-base", label: "¿Qué es?" },
  { to: "/efectos", label: "Efectos" },
  { to: "/test-dependencia", label: "Test" },
  { to: "/tratamientos", label: "Tratamientos" },
  { to: "/blog", label: "Blog" },
  { to: "/preguntas-frecuentes", label: "FAQ" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "border-slate-800 bg-slate-950/85 backdrop-blur-md"
          : "border-transparent bg-slate-950/40 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
        <Link
          to="/"
          data-testid="nav-logo"
          className="group flex items-center gap-2 text-slate-50"
        >
          <span className="grid h-8 w-8 place-items-center rounded-md bg-emerald-500/15 ring-1 ring-emerald-500/40 transition group-hover:bg-emerald-500/25">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
          </span>
          <span className="text-base font-bold tracking-tight">
            pasta-base<span className="text-emerald-400">.cl</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace("/", "")}`}
              className={({ isActive }) =>
                `rounded-md px-3 py-2 text-sm font-medium transition ${
                  isActive
                    ? "text-emerald-400"
                    : "text-slate-300 hover:bg-slate-900 hover:text-slate-50"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="https://sinadicciones.org/centros-de-rehabilitacion-drogas-chile"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta-help"
            className="btn-press hidden items-center gap-2 rounded-full bg-red-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-red-500/20 hover:bg-red-600 sm:inline-flex"
          >
            <LifeBuoy className="h-4 w-4" />
            Buscar Ayuda
          </a>
          <button
            type="button"
            aria-label="Abrir menú"
            data-testid="mobile-menu-toggle"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-800 bg-slate-900 text-slate-200 lg:hidden"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-800 bg-slate-950 lg:hidden" data-testid="mobile-menu">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm font-medium ${
                    isActive
                      ? "bg-slate-900 text-emerald-400"
                      : "text-slate-300 hover:bg-slate-900"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <a
              href="https://sinadicciones.org/centros-de-rehabilitacion-drogas-chile"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-cta-help"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-red-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-600"
            >
              <LifeBuoy className="h-4 w-4" />
              Buscar Ayuda
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
